import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as Scrivito from "scrivito";
import { useItemsPerPage, useUserDepartments } from "hooks";
import { getHomepage } from "utils";
import { PROJECTS_PER_PAGE } from "variables";
import { useProjectListQuery } from "../hooks/useProjectListQuery";

export const ProjectListContext = createContext();

export const ProjectListProvider = Scrivito.connect(({ children, page }) => {
  const {
    itemsPerPage,
    handleItemsPerPageChange,
    isItemsPerPageChanged,
    setIsItemsPerPageChanged,
  } = useItemsPerPage(PROJECTS_PER_PAGE);

  const [currentPageNo, setCurrentPageNo] = useState(() =>
    isItemsPerPageChanged ? 1 : Number(Scrivito.currentPageParams().page) || 1,
  );
  const home = getHomepage();
  const { projectId } = Scrivito.currentPageParams();

  const handleSetCurrentPage = useCallback(
    (pageToSet = 1) => {
      setIsItemsPerPageChanged(false);
      setCurrentPageNo(pageToSet);
      setProjectsToDelete([]);
      setIsSelectAll();

      Scrivito.navigateTo(() => Scrivito.currentPage(), {
        params: {
          page: String(pageToSet),
          ...(projectId && {
            projectId,
          }),
        },
      });
    },
    [setIsItemsPerPageChanged, projectId],
  );

  useEffect(() => {
    if (isItemsPerPageChanged) {
      setCurrentPageNo(1);
      Scrivito.navigateTo(() => Scrivito.currentPage(), {
        params: { page: "1" },
      });
    }
  }, [isItemsPerPageChanged]);

  const [isOrderDescending, setIsOrderDescending] = useState(true);
  const order = isOrderDescending ? "updatedAt desc" : "updatedAt asc";
  const [filters, setFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState(null);
  const [sharedStatusSwitcher, setSharedStatusSwitcher] = useState(null);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [projectsToDelete, setProjectsToDelete] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  const {
    selected: selectedDepartment,
    setSelected: setSelectedDepartment,
    options: departmentOptions,
  } = useUserDepartments();

  const projectListQueryParams = useMemo(
    () => ({
      limit: itemsPerPage || 10,
      page: currentPageNo || 1,
      order,
      filters: {
        searchQuery,
        statuses: filterStatuses,
        sharedStatusSwitcher,
        departmentId: selectedDepartment?.value,
      },
    }),
    [
      currentPageNo,
      filterStatuses,
      itemsPerPage,
      order,
      searchQuery,
      selectedDepartment?.value,
      sharedStatusSwitcher,
    ],
  );

  // Get project list and detect changes that invoke refetching the project list
  const {
    isLoading,
    error,
    refetch: refetchProjectList,
    data: {
      filteredCount,
      statusColors,
      projects,
      pagesCount,
      projectStatuses,
      statistics,
      overallStatistics,
      projectsCount,
    },
  } = useProjectListQuery({ variables: projectListQueryParams });

  // resets projectFilters when overallStatistics updates
  const resetCallback = useCallback(() => setFilters([]), []);

  const projectConfiguratorObj = page.get("redirectToProjectCreate") || page;
  const projectItemObj = page.get("redirectToProjectItem") || page;

  const resetUrlParams = useCallback(() => {
    const projectRefs = home?.get("configLeftSidebarProjectRefs");
    const projectList = projectRefs[2];
    Scrivito.navigateTo(projectList);
  }, [home]);

  const projectListContextValue = useMemo(
    () => ({
      projectsToDelete,
      setProjectsToDelete,
      projects,
      refetchProjectList,
      currentPageNo,
      setCurrentPageNo,
      resetUrlParams,
      projectId,
      projectsCount,
      itemsPerPage,
      resetCallback,
      searchQuery,
      setSearchQuery,
      overallStatistics,
      setSharedStatusSwitcher,
      projectStatuses,
      setFilterStatuses,
      setFilters,
      filters,
      statistics,
      handleItemsPerPageChange,
      error,
      isLoading,
      isOrderDescending,
      projectItemObj,
      projectConfiguratorObj,
      setIsOrderDescending,
      statusColors,
      filteredCount,
      pagesCount,
      setIsItemsPerPageChanged,
      selectedDepartment,
      setSelectedDepartment,
      departmentOptions,
      handleSetCurrentPage,
      isSelectAll,
      setIsSelectAll,
      projectListQueryParams,
    }),
    [
      projectsToDelete,
      setProjectsToDelete,
      projects,
      refetchProjectList,
      currentPageNo,
      setCurrentPageNo,
      resetUrlParams,
      projectId,
      projectsCount,
      itemsPerPage,
      resetCallback,
      searchQuery,
      setSearchQuery,
      overallStatistics,
      setSharedStatusSwitcher,
      projectStatuses,
      setFilterStatuses,
      setFilters,
      filters,
      statistics,
      handleItemsPerPageChange,
      error,
      isLoading,
      isOrderDescending,
      projectItemObj,
      projectConfiguratorObj,
      setIsOrderDescending,
      statusColors,
      filteredCount,
      pagesCount,
      setIsItemsPerPageChanged,
      selectedDepartment,
      setSelectedDepartment,
      departmentOptions,
      handleSetCurrentPage,
      isSelectAll,
      setIsSelectAll,
      projectListQueryParams,
    ],
  );

  return (
    <ProjectListContext.Provider value={projectListContextValue}>
      {children}
    </ProjectListContext.Provider>
  );
});
